// frontend/packages/business-console/src/components/build-interview/QuestionCard.jsx
//
// Centre column of the build interview (Website Builder Redesign, Phase 1): the
// STEP pill, an AI prompt bubble (sparkle + warm headline + the server's prompt
// as helper text), tap-to-add example chips, and the answer surface (the
// restyled freeform AnswerField, or the step's structured widget passed in by
// the page). An editing banner shows when re-answering a completed step.
//
// The page (BuildInterview.jsx) still owns the data + submit handlers; this is
// presentation. Colors via useTokens (auto light/dark). Copy is em-dash-free.

import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import useTokens from '../primitives/useTokens';
import { SparkleTile } from './InterviewShell';
import { stepMeta } from './stepMeta';
import ExampleChips from './ExampleChips';
import AnswerField from './AnswerField';

export default function QuestionCard({
  step,
  totalSteps = 10,
  editing = false,
  onCancelEdit,
  busy = false,
  onSubmitText,
  onBack,
  structuredNode = null,
  skippable = false,
  onSkip,
  // The customer's previously-saved freeform answer for this step, so editing
  // a completed step prefills what they wrote instead of an empty box. The page
  // keys this component on step id, so it remounts per step and useState picks
  // the right initial value up.
  initialText = '',
}) {
  const t = useTokens();
  const meta = stepMeta(step.id);
  const isFreeform =
    !structuredNode && step.modes && step.modes.includes('freeform');
  const [text, setText] = useState(initialText);
  const textRef = useRef(null);

  const insertChip = (scaffold) => {
    setText(
      (prev) => (prev && !prev.endsWith('\n') ? `${prev}\n` : prev) + scaffold
    );
    if (textRef.current) textRef.current.focus();
  };

  return (
    <Box>
      {editing ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busy}
              onClick={onCancelEdit}
            >
              Back to where I left off
            </Button>
          }
        >
          Editing this step. Your answer is filled in below. Change it and save,
          or head back to pick up where you left off. Nothing changes unless you
          save.
        </Alert>
      ) : null}

      {/* STEP pill + name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box
          sx={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '.04em',
            color: t.blue,
            background: t.blueBg,
            border: `1px solid ${t.blueBorder}`,
            borderRadius: '999px',
            padding: '3px 10px',
            textTransform: 'uppercase',
          }}
        >
          Step {step.order} of {totalSteps}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: t.faint }}>
          {step.title}
        </Typography>
      </Box>

      {/* AI prompt bubble */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <SparkleTile size={34} radius={10} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 21,
              fontWeight: 900,
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              color: t.text,
            }}
          >
            {meta.headline || step.title}
          </Typography>
          {step.prompt ? (
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.6,
                color: t.textSecondary,
                mt: 0.75,
              }}
            >
              {step.prompt}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Example chips (freeform steps only) */}
      {isFreeform && meta.chips.length ? (
        <Box sx={{ mb: 2 }}>
          <ExampleChips
            chips={meta.chips}
            onInsert={insertChip}
            disabled={busy}
          />
        </Box>
      ) : null}

      {/* Answer surface */}
      {structuredNode ? (
        structuredNode
      ) : isFreeform ? (
        <AnswerField
          value={text}
          onChange={setText}
          busy={busy}
          inputRef={textRef}
        />
      ) : null}

      {/* Action row: Back / Skip ... Saved ... Save & continue (freeform) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 2,
          flexWrap: 'wrap',
        }}
      >
        {onBack ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            disabled={busy}
            onClick={onBack}
            sx={{ color: t.muted }}
          >
            Back
          </Button>
        ) : null}
        {skippable ? (
          <Button
            size="small"
            disabled={busy}
            onClick={onSkip}
            sx={{ color: t.muted }}
          >
            Skip this step
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Box
          role="status"
          aria-live="polite"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <CheckIcon sx={{ fontSize: 14, color: t.green }} aria-hidden />
          <Typography sx={{ fontSize: 12, color: t.green }}>
            Saved automatically
          </Typography>
        </Box>
        {isFreeform ? (
          <Button
            variant="contained"
            disabled={busy || !text.trim()}
            endIcon={<ArrowForwardIcon />}
            onClick={() => onSubmitText && onSubmitText(text.trim())}
            sx={{
              background: t.gradient,
              borderRadius: '10px',
              fontWeight: 800,
              px: 2.25,
              boxShadow: '0 6px 16px rgba(37,99,235,.3)',
              '&:hover': { background: t.gradient, filter: 'brightness(1.05)' },
            }}
          >
            {busy
              ? 'Reading your answer'
              : editing
                ? 'Save changes'
                : 'Save & continue'}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
